import * as React from 'react';
import Text from '../../shared/Text';
import FormValidationMessage from './form-validation-message';
import { sendEmptyTextIfNull } from '../utils/input-handlers';
import { validatePassword, ComplexityError } from '@c42/password-complexity';
import { PasswordComplexity } from './password-complexity';
import { useLocation } from 'react-router';
import { useSetInitialQueryParams } from './providers/routing/hooks';
import {
  useOnSubmitPasswordReset,
  useResetPasswordData,
  useSetResetPasswordData,
} from './providers/resetPassword/hooks';
import { TextField } from '@mui/material';
import { FormFieldWrapper, FormSubmitWrapper, FormWrapper } from './shared-form-components';
import { AsyncButton } from '@c42/component-library';

const ResetPasswordWrapper: React.FC = () => {
  const location = useLocation();
  const setInitialQueryParams = useSetInitialQueryParams();

  React.useEffect(() => {
    const queryParams = Object.fromEntries(new URLSearchParams(location.search));
    // in case the user was already on the login app and copied/pasted
    // a reset url to this tab, we need to re-initialize the query params in state
    setInitialQueryParams(queryParams);
  }, []);

  return <ResetPassword />;
};

const ResetPassword: React.FC = () => {
  const onSubmitPasswordReset = useOnSubmitPasswordReset();
  const setResetPasswordData = useSetResetPasswordData();
  const { password, confirmPassword, error, isUpdatingPassword } = useResetPasswordData();

  const complexityErrors = validatePassword(password);

  const isConfirmPasswordEmpty = !confirmPassword;
  const isPasswordMatching = password === confirmPassword;
  const isConfirmPasswordInvalid = confirmPassword != null && (isConfirmPasswordEmpty || !isPasswordMatching);
  const isFormValid = !(complexityErrors.length || !isPasswordMatching);

  let passwordErrorText;
  if (complexityErrors.indexOf(ComplexityError.EMPTY) >= 0) {
    passwordErrorText = Text.get('_validate_password_required');
  } else if (complexityErrors.length) {
    passwordErrorText = Text.get('_validate_password_requirements');
  }

  let confirmPasswordErrorText;
  if (isConfirmPasswordInvalid && isConfirmPasswordEmpty) {
    confirmPasswordErrorText = Text.get('_validate_password_confirm_required');
  } else if (isConfirmPasswordInvalid && !isPasswordMatching) {
    confirmPasswordErrorText = Text.get('login_error_passwordConfirm');
  }

  const submitForm = (password: string) => {
    onSubmitPasswordReset(password);
  };

  const submitHandler = (
    submitForm: (password: string) => void,
    password: string | null,
    e: React.FormEvent<any>,
  ) => {
    e.preventDefault();
    submitForm(password);
  };

  const changePasswordHandler: React.FormEventHandler<any> = (event) => {
    event.persist?.();
    setResetPasswordData((current) => ({
      ...current,
      password: (event.target as HTMLInputElement).value,
    }));
  };

  const changeConfirmPasswordHandler: React.FormEventHandler<any> = (event) => {
    event.persist?.();
    setResetPasswordData((current) => ({
      ...current,
      confirmPassword: (event.target as HTMLInputElement).value,
    }));
  };

  return (
    <FormWrapper>
      <form onSubmit={submitHandler.bind(null, submitForm, password)} name="resetPasswordFrm" noValidate>
        <FormValidationMessage message={error} />

        <FormFieldWrapper>
          <TextField
            autoComplete="off"
            disabled={isUpdatingPassword}
            error={Boolean(passwordErrorText)}
            fullWidth
            helperText={passwordErrorText}
            inputProps={{ 'data-test': 'password-input' }}
            label={Text.get('login_new_password')}
            onBlur={sendEmptyTextIfNull.bind(null, changePasswordHandler, password)}
            onChange={changePasswordHandler}
            type="password"
            value={password || ''}
          />
        </FormFieldWrapper>

        <FormFieldWrapper>
          <TextField
            autoComplete="off"
            disabled={isUpdatingPassword}
            error={Boolean(confirmPasswordErrorText)}
            fullWidth
            helperText={confirmPasswordErrorText}
            inputProps={{ 'data-test': 'password-confirm-input' }}
            label={Text.get('login_confirm_new_password')}
            onBlur={sendEmptyTextIfNull.bind(null, changeConfirmPasswordHandler, confirmPassword)}
            onChange={changeConfirmPasswordHandler}
            type="password"
            value={confirmPassword || ''}
          />
        </FormFieldWrapper>

        <PasswordComplexity errors={complexityErrors} />

        <FormSubmitWrapper>
          <AsyncButton
            data-test="submit"
            disabled={isUpdatingPassword || !isFormValid}
            isLoading={isUpdatingPassword}
            type="submit"
            variant="contained"
          >
            {Text.get('login_reset_password')}
          </AsyncButton>
        </FormSubmitWrapper>
      </form>
    </FormWrapper>
  );
};

export default ResetPasswordWrapper;
